import {
  BoxGeometry,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  type Scene,
  TorusGeometry,
  Vector3,
} from 'three';
import type { ZoneDef } from '../game/zones';
import type { ScenePalette } from './environment';
import { buildLane, type LaneSpec } from './laneBuilder';
import { loadMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import { generateBoxUVs } from './ramps';
import type { BuiltLevel } from './TestLevel';

/**
 * "The Spillway" — a new in-engine surf course distilled from the CS surf canon
 * (the bundled .bsp imports were retired). It takes the genre's best concepts and
 * rebuilds them with our own proven prism/lane geometry, so it surfs reliably:
 *
 *   - a flowing, continuous carving line (surf_utopia's clean minimalism);
 *   - staged tiers that narrow + steepen as you descend (surf_kitsune's stages);
 *   - left/right slalom that trains air-strafe turn-holding (surf_aircontrol);
 *   - one unbroken line from the drop-in to the runout (surf_lt_omnific's "never
 *     touch the ground" ideal).
 *
 * Three `buildLane` "acts" (T1 → T3) chain off each other's end cursor into a
 * single winding river of ramp, merged per-colour into a few draw calls. Untimed
 * free-surf with a teleport loop back to the top; miss the line and the lava floor
 * resets you. Surfability is sim-tested (Spillway.test.ts). Water-blue theme.
 */

export const SPILLWAY_ID = 'surf_spillway';

/** Cool blue-green water atmosphere, distinct from the snow Fall Line. */
export const spillwayPalette: ScenePalette = {
  background: 0x0e2a3a,
  fogColor: 0x123448,
  fogDensity: 0.00003,
  sunColor: 0xdfeffb,
  sunIntensity: 2.4,
  envIntensity: 0.95,
  ambientColor: 0x2a4a5a,
};

/** Drop-in cursor (capsule-feet frame); the course flows -z then slaloms down. */
const START = new Vector3(0, 9000, 3000);
const START_HEADING = 180; // travel -z (entry cross-axis = x)

/** Three tiers chained head-to-tail: wide/gentle → tighter → narrow/steep. */
const ACTS: (LaneSpec & { hue: number })[] = [
  {
    hue: 0,
    width: 1100,
    angleDeg: 54,
    declineDeg: 10,
    segments: [
      { length: 3000 },
      { length: 1600, turnDeg: -55 },
      { length: 1400 },
      { length: 1600, turnDeg: 60 },
    ],
  },
  {
    hue: 1,
    width: 900,
    angleDeg: 56,
    declineDeg: 12,
    segments: [
      { length: 1200 },
      { length: 1800, turnDeg: -80 },
      { length: 1200 },
      { length: 1500, turnDeg: 70 },
    ],
  },
  {
    hue: 2,
    width: 760,
    angleDeg: 58,
    declineDeg: 13,
    segments: [
      { length: 1000 },
      { length: 1400, turnDeg: -60 },
      { length: 1000 },
      { length: 1400, turnDeg: 55 },
      { length: 1800 },
    ],
  },
];

const TINTS = [0x3bc9db, 0x4dabf7, 0x5c7cfa]; // teal → azure → deep blue

export async function buildSpillway(scene: Scene): Promise<BuiltLevel> {
  const materials = await loadMaterials();
  const platformMaterial = materials.platform;
  const tintCache = new Map<number, MeshStandardMaterial>();
  const hue = (i: number): MeshStandardMaterial => {
    const hex = TINTS[i % TINTS.length] as number;
    let m = tintCache.get(hex);
    if (!m) {
      m = (materials.ramp as MeshStandardMaterial).clone();
      m.color.setHex(hex);
      tintCache.set(hex, m);
    }
    return m;
  };

  const colliders: Mesh[] = [];
  const toMerge: Mesh[] = [];

  // Ground — "the floor is lava": touch it and you respawn to the top.
  const groundGeometry = new PlaneGeometry(40000, 40000);
  groundGeometry.computeVertexNormals();
  generateBoxUVs(groundGeometry, 512);
  const ground = new Mesh(groundGeometry, materials.ground);
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);
  colliders.push(ground);

  const platform = (x: number, y: number, z: number, size: number): void => {
    const p = new Mesh(new BoxGeometry(size, 48, size), platformMaterial);
    p.position.set(x, y, z);
    scene.add(p);
    toMerge.push(p);
  };

  // Spawn platform at the top; the first band starts just below it.
  platform(START.x, START.y + 220, START.z, 900);

  // Chain the three acts into one continuous winding descent.
  let cursor = START.clone();
  let heading = START_HEADING;
  const seams: Vector3[] = [];
  for (const act of ACTS) {
    const run = buildLane(act, cursor, heading, hue(act.hue));
    for (const mesh of run.meshes) {
      mesh.castShadow = false; // many segments — shadows are a disproportionate cost
      mesh.receiveShadow = true;
      scene.add(mesh);
      toMerge.push(mesh);
    }
    cursor = run.cursor;
    heading = run.headingDeg;
    seams.push(cursor.clone());
  }

  // Base plaza the final band coasts onto, ahead of the last exit.
  const h = (heading * Math.PI) / 180;
  const plaza = new Vector3(
    cursor.x + Math.sin(h) * 1800,
    cursor.y - 120,
    cursor.z + Math.cos(h) * 1800,
  );
  platform(plaza.x, plaza.y, plaza.z, 4200);

  colliders.push(...mergeStaticMeshes(scene, toMerge));

  // Glowing gateways framing the drop-in + each tier seam (visual only).
  const hoop = (color: number, pos: Vector3, radius: number): void => {
    const ring = new Mesh(
      new TorusGeometry(radius, radius * 0.06, 16, 40),
      new MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.5, roughness: 0.4 }),
    );
    ring.position.copy(pos);
    ring.rotation.x = Math.PI / 2;
    ring.castShadow = false;
    scene.add(ring);
  };
  hoop(0x3bc9db, new Vector3(START.x, START.y + 560, START.z), 1000);
  for (const seam of seams.slice(0, -1)) {
    hoop(0x4dabf7, new Vector3(seam.x, seam.y + 320, seam.z), 760);
  }

  // Teleport loop: coast onto the plaza, warp back to the top with speed.
  const spawn = new Vector3(START.x, START.y + 290, START.z);
  const teleCenter = new Vector3(plaza.x, plaza.y + 760, plaza.z);
  const teleSize = new Vector3(3600, 1500, 3600);
  const marker = new Mesh(
    new BoxGeometry(teleSize.x, teleSize.y, teleSize.z),
    new MeshStandardMaterial({
      color: 0xab47bc,
      emissive: 0xab47bc,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.1,
    }),
  );
  marker.position.copy(teleCenter);
  scene.add(marker); // visual only — never a collider

  const zones: ZoneDef[] = [
    { kind: 'teleport', center: teleCenter, size: teleSize, dest: spawn.clone() },
  ];

  return {
    mapId: SPILLWAY_ID,
    colliders,
    spawn,
    spawnYaw: 0, // faces -z, down the first band
    zones,
    killY: 40,
  };
}
